import {Component, OnInit, OnDestroy, ViewEncapsulation, HostListener, signal, computed, inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {BoService} from '../services/bo.service';
import {CmlHeaderComponent} from './cml-header/cml-header.component';
import {CmlEventAccordionComponent} from './cml-event-accordion/cml-event-accordion.component';
import {TplSource, EventData, TplConfig, SourceType, EventWithSources, ControlSchedule, allSelected, DEFAULT_CONFIRMATION_COLOR} from './cml-types';
import {parseEventDate, sortEventsForDisplay} from './cml-event-order';

@Component({
  selector: 'app-configurer-ma-liste',
  templateUrl: './configurer-ma-liste.component.html',
  styleUrls: ['./configurer-ma-liste.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CmlHeaderComponent, CmlEventAccordionComponent]
})
export class ConfigurerMaListeComponent implements OnInit, OnDestroy {
  private boService = inject(BoService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  // --- State ---
  events = signal<EventWithSources[]>([]);
  tplConfigs = signal<Record<string, TplConfig>>({});
  searchTerm = signal('');
  expandedEventIds = signal<string[]>([]);
  expandedSourceTypes = signal<Record<string, SourceType | null>>({});
  loading = signal(true);
  saving = signal(false);
  hidePastEvents = signal(true);
  selectedLabels = signal<string[]>([]);
  selectedSchedule = signal<string | null>(null);
  nowModeActive = signal(true);

  private nowInterval: ReturnType<typeof setInterval> | undefined;
  private searchTimeout: ReturnType<typeof setTimeout> | undefined;
  private canSeeLabelsSchedules = false;
  private nowTick = signal(0);

  // --- Computed ---
  changeCount = computed(() => {
    const configs = this.tplConfigs();
    let count = 0;
    for (const id of Object.keys(configs)) {
      const c = configs[id];
      if (c.checked !== c.original.checked ||
        c.scanNbr !== c.original.scanNbr ||
        c.infinity !== c.original.infinity ||
        c.nfcMode !== c.original.nfcMode ||
        c.colorConfirmation !== c.original.colorConfirmation) {
        count++;
      }
    }
    return count;
  });

  hasChanges = computed(() => this.changeCount() > 0);

  filteredEvents = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const hidePast = this.hidePastEvents();
    this.nowTick(); // dependency pour forcer le recompute quand l'heure change (mode Maintenant)
    let list = this.events();
    if (hidePast) list = list.filter(ev => this.isActive(ev));

    // Build combined predicate (S5 — single applyTemplateFilter call)
    const predicates: Array<(tpl: TplSource, type: SourceType) => boolean> = [];
    predicates.push((tpl) => this.isTplVisible(tpl));
    const labels = this.selectedLabels();
    if (labels.length > 0) {
      predicates.push((tpl) => {
        const tplLabel = tpl.controlLabel?.trim().toLowerCase();
        return tplLabel !== undefined && labels.some(l => l.toLowerCase() === tplLabel);
      });
    }
    const scheduleKey = this.nowModeActive() ? this.getCurrentScheduleKey() : this.selectedSchedule();
    if (scheduleKey) {
      predicates.push((tpl) => this.matchesScheduleFilter(tpl, scheduleKey));
    }
    list = this.applyTemplateFilter(list, (tpl, type) => predicates.every(p => p(tpl, type)));

    if (term) list = this.filterSourcesBySearch(list, term);
    return list;
  });

  pastEventsCount = computed(() => this.events().filter(ev => !this.isActive(ev)).length);

  availableLabels = computed(() => {
    if (!this.canSeeLabelsSchedules) return [];
    const labelMap = new Map<string, {label: string; color: string; count: number}>();
    for (const ev of this.events()) {
      for (const s of ev.sources) {
        for (const tpl of s.templates) {
          if (!this.isTplVisible(tpl)) continue;
          if (tpl.controlLabel) {
            const key = tpl.controlLabel.trim().toLowerCase();
            if (!labelMap.has(key)) {
              labelMap.set(key, {label: tpl.controlLabel.trim(), color: tpl.controlColor || '', count: 1});
            } else {
              labelMap.get(key)!.count++;
            }
          }
        }
      }
    }
    return Array.from(labelMap.values()).sort((a, b) => b.count - a.count);
  });

  availableSchedules = computed(() => {
    if (!this.canSeeLabelsSchedules) return [];
    const schedules: Array<{key: string; label: string; type: string}> = [];
    const seen = new Set<string>();
    for (const ev of this.events()) {
      for (const s of ev.sources) {
        for (const tpl of s.templates) {
          if (!this.isTplVisible(tpl)) continue;
          if (!tpl.controlSchedule) continue;
          for (const slot of tpl.controlSchedule.slots) {
            const key = this.scheduleSlotKey(slot, tpl.controlSchedule.type);
            if (key && !seen.has(key)) {
              seen.add(key);
              schedules.push({key, label: this.scheduleSlotLabel(slot, tpl.controlSchedule.type), type: tpl.controlSchedule.type});
            }
          }
        }
      }
    }
    return schedules.sort((a, b) => a.key.localeCompare(b.key));
  });

  hiddenPastSearchCount = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term || !this.hidePastEvents()) return 0;
    this.nowTick();
    const pastEvents = this.events().filter(ev => !this.isActive(ev));
    const predicates: Array<(tpl: TplSource, type: SourceType) => boolean> = [];
    predicates.push((tpl) => this.isTplVisible(tpl));
    const labels = this.selectedLabels();
    if (labels.length > 0) {
      predicates.push((tpl) => {
        const tplLabel = tpl.controlLabel?.trim().toLowerCase();
        return tplLabel !== undefined && labels.some(l => l.toLowerCase() === tplLabel);
      });
    }
    const scheduleKey = this.nowModeActive() ? this.getCurrentScheduleKey() : this.selectedSchedule();
    if (scheduleKey) {
      predicates.push((tpl) => this.matchesScheduleFilter(tpl, scheduleKey));
    }
    const withFilters = this.applyTemplateFilter(pastEvents, (tpl, type) => predicates.every(p => p(tpl, type)));
    const matched = this.filterSourcesBySearch(withFilters, term);
    return matched.reduce((sum, ev) => sum + ev.sources.reduce((s, src) => s + src.templates.length, 0), 0);
  });

  private applyTemplateFilter(list: EventWithSources[], predicate: (tpl: TplSource, sourceType: SourceType) => boolean): EventWithSources[] {
    return list.map(ev => {
      const sources = ev.sources.map(source => {
        const filtered = source.templates.filter(tpl => predicate(tpl, source.type));
        if (filtered.length === source.templates.length) return source;
        return {...source, templates: filtered};
      });
      const nonEmpty = sources.filter(s => s.templates.length > 0);
      if (nonEmpty.length === ev.sources.length && sources.every((s, i) => s === ev.sources[i])) return ev;
      return {...ev, sources: nonEmpty};
    }).filter(ev => ev.sources.length > 0);
  }

  private matchesGroupName(tpl: TplSource, ev: EventWithSources, term: string): boolean {
    const gId = tpl.idGroup || tpl.idTplGroup;
    if (!gId || gId === 'no-group' || gId === 'none') {
      return 'groupe par défaut'.includes(term);
    }
    const group = ev.tplGroups?.find(g => g._id === gId);
    if (!group) return 'groupe par défaut'.includes(term);
    return group.name.toLowerCase().includes(term);
  }

  private filterSourcesBySearch(list: EventWithSources[], term: string): EventWithSources[] {
    return list.map(ev => {
      if (ev.name.toLowerCase().includes(term)) return ev;
      const sources = ev.sources.map(source => {
        const filtered = source.templates.filter(tpl =>
          tpl.name.toLowerCase().includes(term) ||
          (source.type === 'online' && this.matchesGroupName(tpl, ev, term))
        );
        if (filtered.length === source.templates.length) return source;
        return {...source, templates: filtered};
      });
      const nonEmpty = sources.filter(s => s.templates.length > 0);
      if (nonEmpty.length === 0) return null;
      return {...ev, sources: nonEmpty};
    }).filter((ev): ev is EventWithSources => ev !== null);
  }

  private isTplVisible(tpl: TplSource): boolean {
    if (tpl.draft === true) return false;
    if (tpl.statut === 'garbage' && (tpl.ticketCount ?? 0) === 0) return false;
    return true;
  }

  private isActive(ev: EventData): boolean {
    const endTime = parseEventDate(ev.endDate);
    if (endTime === null) return true;
    // Seul le jour de fin compte : un événement reste actif jusqu'à la fin de sa dernière journée.
    const end = new Date(endTime);
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return end.getTime() >= today.getTime();
  }

  togglePastEvents() {
    this.hidePastEvents.update(v => !v);
    this.syncUrlParams();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    const headerbo = document.querySelector('.headerbo') as HTMLElement;
    if (!headerbo) return;
    if (window.scrollY > 120) {
      headerbo.style.borderColor = 'transparent';
      headerbo.style.boxShadow = 'var(--ui-shadow-sm)';
    } else {
      headerbo.style.borderColor = 'var(--ui-color-border-muted)';
      headerbo.style.boxShadow = 'none';
    }
  }

  ngOnInit() {
    // Shell setup (legacy BO)
    const menubo = document.querySelector('.menubo') as HTMLElement;
    const headerbo = document.querySelector('.headerbo') as HTMLElement;
    if (menubo) menubo.style.display = 'flex';
    if (headerbo) headerbo.style.display = 'flex';

    const htitsec = document.getElementById('htitsec');
    const htitsec2 = document.getElementById('htitsec2');
    if (htitsec) htitsec.innerHTML = 'Configurer';
    if (htitsec2) htitsec2.innerHTML = 'Ma liste';

    document.querySelectorAll('.menubs').forEach(el => (el as HTMLElement).id = '');
    const menubs3 = document.querySelector('.menubs-3');
    if (menubs3) menubs3.id = 'actif1';
    document.querySelectorAll('.secmenu2bs').forEach(el => (el as HTMLElement).id = '');
    const s2 = document.getElementsByClassName('3s2')[0] as HTMLElement | undefined;
    if (s2) s2.id = 'actif2';

    const wiw = window.innerWidth;
    if (wiw > 610) {
      [htitsec, htitsec2, document.getElementById('fles')].forEach(el => { if (el) (el as HTMLElement).style.display = 'inline'; });
    } else {
      if (htitsec2) htitsec2.style.display = 'inline';
      const fles = document.getElementById('fles');
      if (fles) fles.style.display = 'none';
    }

    if (headerbo) {
      headerbo.style.borderColor = 'var(--ui-color-border-muted)';
      headerbo.style.boxShadow = 'none';
    }
    window.scrollTo(0, 0);

    this.canSeeLabelsSchedules = localStorage.getItem('isCanary') === 'true' || localStorage.getItem('isStaff') === 'true';
    if (!this.canSeeLabelsSchedules) {
      this.nowModeActive.set(false);
    }

    // Read deep-link query params
    const params = this.route.snapshot.queryParams;
    if (this.canSeeLabelsSchedules) {
      if (params['labels']) {
        this.selectedLabels.set(params['labels'].split(','));
      }
      if (params['schedule']) {
        this.nowModeActive.set(false);
        this.selectedSchedule.set(params['schedule']);
      }
    }
    if (params['hidePast'] === 'false') {
      this.hidePastEvents.set(false);
    }

    // Load data
    const dataList = JSON.parse(localStorage.getItem('dataList') || '{}');
    const dataListTpl: {id: string; scanNbr: number; infinity?: boolean; nfcMode?: string; colorConfirmation?: string; accreditation?: boolean}[] = dataList.tpl || [];
    const darkMode = JSON.parse(localStorage.getItem('infoUser') || '{}').darkMode === 'on' ? 'on' : 'off';
    const contrastMode = JSON.parse(localStorage.getItem('infoUser') || '{}').contrastMode === 'on' ? 'on' : 'off';

    this.boService.getAllTpl().then((data: any) => { // any-justifié: BoService non typé, retour CouchDB brut
      const eventsData: EventData[] = data || [];

      const eventsWithSources: EventWithSources[] = eventsData.map(ev => ({
        ...ev,
        sources: [
          // #4509 : un tarif d'import (nouvelle génération) est un tplTicket standard, donc un
          // tarif de billetterie en ligne normal. On fusionne tplImport dans la source 'online'
          // (getGroups les range dans leur groupe ou le Groupe par défaut) au lieu de les isoler
          // dans une section 'Importations' dédiée.
          {type: 'online' as SourceType, label: 'Billetterie en ligne', icon: 'fas fa-ticket-alt', templates: [...(ev.tplOnline || []), ...(ev.tplImport || [])], emptyMessage: 'Aucun tarif configuré pour la billetterie en ligne de cet événement'},
          {type: 'onsite' as SourceType, label: 'Billetterie sur place', icon: 'fas fa-cash-register', templates: ev.tplOnsite || [], emptyMessage: 'Aucun tarif configuré pour la billetterie sur place de cet événement'}
        ]
      }));
      this.events.set(sortEventsForDisplay(eventsWithSources));

      const configs: Record<string, TplConfig> = {};
      const listTplMap = new Map(dataListTpl.map(t => [t.id, t]));

      for (const ev of eventsData) {
        const allTpls = [...(ev.tplOnline || []), ...(ev.tplOnsite || []), ...(ev.tplImport || [])];
        for (const tpl of allTpls) {
          const saved = listTplMap.get(tpl._id);
          const checked = !!saved;
          const scanNbr = saved ? saved.scanNbr : 0;
          const infinity = saved?.infinity === true;
          const nfcMode = saved?.nfcMode || 'inherit';
          // Le vert par défaut est normalisé en '' (non persisté dans le doc liste)
          const savedColor = saved?.colorConfirmation || '';
          const colorConfirmation = savedColor.toLowerCase() === DEFAULT_CONFIRMATION_COLOR ? '' : savedColor;
          configs[tpl._id] = {
            checked,
            scanNbr,
            infinity,
            nfcMode,
            colorConfirmation,
            accreditation: saved?.accreditation === true,
            original: {checked, scanNbr, infinity, nfcMode, colorConfirmation}
          };
        }
      }
      this.tplConfigs.set(configs);
      this.loading.set(false);
      this.boService.screenMode(darkMode, contrastMode);
      if (this.canSeeLabelsSchedules && this.nowModeActive()) this.startNowInterval();

      const loader = document.querySelector('.blocLoader') as HTMLElement;
      if (loader) {
        loader.style.transition = 'opacity 0.3s';
        loader.style.opacity = '0';
        setTimeout(() => loader.style.display = 'none', 300);
      }
    }).catch((error: any) => { // any-justifié: erreur HTTP/CouchDB sans shape garantie
      console.log(error);
      this.boService.errorLoad(error);
    });
  }

  // --- Expand/collapse ---
  toggleEvent(eventId: string) {
    this.expandedEventIds.update(ids =>
      ids.includes(eventId) ? ids.filter(id => id !== eventId) : [...ids, eventId]
    );
  }

  toggleSource(ev: {eventId: string; sourceType: SourceType}) {
    this.expandedSourceTypes.update(map => {
      const current = map[ev.eventId];
      return {...map, [ev.eventId]: current === ev.sourceType ? null : ev.sourceType};
    });
  }

  // --- Toggle actions ---
  toggleEventCheck(ev: EventWithSources) {
    const shouldCheck = !this.isEventChecked(ev);
    const allTpls: TplSource[] = [];
    for (const s of ev.sources) { allTpls.push(...s.templates); }
    this.setTplsChecked(allTpls, shouldCheck);
  }

  toggleSourceCheck(templates: TplSource[]) {
    const shouldCheck = !this.isSourceChecked(templates);
    this.setTplsChecked(templates, shouldCheck);
  }

  toggleTplCheck(tplId: string) {
    if (this.isManagedByBackstage(tplId)) return;
    const configs = {...this.tplConfigs()};
    const c = {...configs[tplId]};
    c.checked = !c.checked;
    if (c.checked) {
      if (c.scanNbr < 1) c.scanNbr = 1;
    } else {
      c.scanNbr = 0;
      c.infinity = false;
    }
    configs[tplId] = c;
    this.tplConfigs.set(configs);
  }

  private isEventChecked(ev: EventWithSources): boolean {
    const total = ev.sources.reduce((sum, s) => sum + s.templates.length, 0);
    const checked = ev.sources.reduce((sum, s) =>
      sum + s.templates.filter(t => this.tplConfigs()[t._id]?.checked).length, 0);
    return allSelected(checked, total);
  }

  private isSourceChecked(templates: TplSource[]): boolean {
    const checked = templates.filter(t => this.tplConfigs()[t._id]?.checked).length;
    return allSelected(checked, templates.length);
  }

  /** #9400 : un type d'accès Backstage est en lecture seule dans BO Contrôle. */
  private isManagedByBackstage(tplId: string): boolean {
    return this.tplConfigs()[tplId]?.accreditation === true;
  }

  private setTplsChecked(templates: TplSource[], checked: boolean) {
    const configs = {...this.tplConfigs()};
    for (const tpl of templates) {
      if (this.isManagedByBackstage(tpl._id)) continue;
      const c = {...configs[tpl._id]};
      c.checked = checked;
      if (checked) {
        if (c.scanNbr < 1) c.scanNbr = 1;
      } else {
        c.scanNbr = 0;
        c.infinity = false;
      }
      configs[tpl._id] = c;
    }
    this.tplConfigs.set(configs);
  }

  // --- Child event handlers ---
  onNfcChangeFromChild(ev: {id: string; value: string}) {
    if (this.isManagedByBackstage(ev.id)) return;
    const configs = {...this.tplConfigs()};
    const c = {...configs[ev.id]};
    c.nfcMode = ev.value;
    if (!c.checked) {
      c.checked = true;
      if (c.scanNbr < 1) c.scanNbr = 1;
    }
    configs[ev.id] = c;
    this.tplConfigs.set(configs);
  }

  onColorChangeFromChild(ev: {id: string; value: string}) {
    if (this.isManagedByBackstage(ev.id)) return;
    const configs = {...this.tplConfigs()};
    const c = {...configs[ev.id]};
    c.colorConfirmation = ev.value;
    configs[ev.id] = c;
    this.tplConfigs.set(configs);
  }

  onScanChangeFromChild(ev: {id: string; value: number}) {
    if (this.isManagedByBackstage(ev.id)) return;
    const configs = {...this.tplConfigs()};
    const c = {...configs[ev.id]};
    c.scanNbr = ev.value;
    if (isNaN(c.scanNbr) || c.scanNbr < 0) c.scanNbr = 1;
    if (c.scanNbr > 0 && !c.checked) {
      c.checked = true;
    } else if (c.scanNbr <= 0 && c.checked) {
      c.checked = false;
      c.infinity = false;
    }
    if (c.scanNbr > 0) c.infinity = false;
    configs[ev.id] = c;
    this.tplConfigs.set(configs);
  }

  // --- Search ---
  onSearchChange(value: string) {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.searchTerm.set(value);
      if (value.trim()) {
        const term = value.trim().toLowerCase();
        const matchIds: string[] = [];
        const sourceMap: Record<string, SourceType | null> = {};
        for (const ev of this.filteredEvents()) {
          const matchingSource = ev.sources.find(s => s.templates.some(t =>
            t.name.toLowerCase().includes(term) ||
            (s.type === 'online' && this.matchesGroupName(t, ev, term))
          ));
          if (matchingSource) {
            matchIds.push(ev._id);
            sourceMap[ev._id] = matchingSource.type;
          }
        }
        this.expandedEventIds.set(matchIds);
        this.expandedSourceTypes.set(sourceMap);
      }
    }, 200);
  }

  showPastResults() {
    this.hidePastEvents.set(false);
  }

  clearSearch() {
    if (this.searchTimeout) { clearTimeout(this.searchTimeout); this.searchTimeout = undefined; }
    this.searchTerm.set('');
    this.expandedEventIds.set([]);
    this.expandedSourceTypes.set({});
  }

  toggleLabel(label: string) {
    this.selectedLabels.update(labels =>
      labels.includes(label) ? labels.filter(l => l !== label) : [...labels, label]
    );
    this.syncUrlParams();
  }

  selectSchedule(key: string | null) {
    this.nowModeActive.set(false);
    this.selectedSchedule.set(key);
    this.syncUrlParams();
  }

  toggleNowMode() {
    this.nowModeActive.update(v => !v);
    if (this.nowModeActive()) {
      this.selectedSchedule.set(null);
      this.startNowInterval();
    } else {
      this.stopNowInterval();
    }
    this.syncUrlParams();
  }

  ngOnDestroy() {
    if (this.nowInterval) clearInterval(this.nowInterval);
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  }

  private startNowInterval() {
    this.stopNowInterval();
    this.nowInterval = setInterval(() => {
      this.nowTick.update(v => v + 1);
    }, 60000);
  }

  private stopNowInterval() {
    if (this.nowInterval) { clearInterval(this.nowInterval); this.nowInterval = undefined; }
  }

  private syncUrlParams() {
    const params = new URLSearchParams();
    const labels = this.selectedLabels();
    if (labels.length > 0) params.set('labels', labels.join(','));
    const schedule = this.nowModeActive() ? null : this.selectedSchedule();
    if (schedule) params.set('schedule', schedule);
    if (!this.hidePastEvents()) params.set('hidePast', 'false');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
  }

  private scheduleSlotKey(slot: {date?: string; startTime?: string; endTime?: string}, type: string): string {
    if (type === 'day') return `day:${slot.date || ''}`;
    if (type === 'hour') return `hour:${slot.startTime || ''}-${slot.endTime || ''}`;
    return `day+hour:${slot.date || ''}:${slot.startTime || ''}-${slot.endTime || ''}`;
  }

  private scheduleSlotLabel(slot: {date?: string; startTime?: string; endTime?: string}, type: string): string {
    if (type === 'day' && slot.date) {
      const [y, m, d] = slot.date.split('-');
      return `${d}/${m}/${y}`;
    }
    if (type === 'hour') return `${slot.startTime || '?'}-${slot.endTime || '?'}`;
    if (type === 'day+hour' && slot.date) {
      const [y, m, d] = slot.date.split('-');
      return `${d}/${m}/${y} ${slot.startTime || '?'}-${slot.endTime || '?'}`;
    }
    return '?';
  }

  private matchesScheduleFilter(tpl: TplSource, scheduleKey: string | null): boolean {
    if (!scheduleKey) return true;
    if (!tpl.controlSchedule) return true;

    const sched = tpl.controlSchedule;
    if (scheduleKey.startsWith('day:')) {
      const filterDate = scheduleKey.slice(4);
      return sched.slots.some(slot => slot.date === filterDate);
    }
    if (scheduleKey.startsWith('hour:')) {
      const filterRange = scheduleKey.slice(5);
      return sched.slots.some(slot => `${slot.startTime}-${slot.endTime}` === filterRange);
    }
    if (scheduleKey.startsWith('day+hour:')) {
      const parts = scheduleKey.slice(9);
      const colonIdx = parts.indexOf(':');
      const filterDate = parts.slice(0, colonIdx);
      const filterRange = parts.slice(colonIdx + 1);
      return sched.slots.some(slot => slot.date === filterDate && `${slot.startTime}-${slot.endTime}` === filterRange);
    }
    return true;
  }

  private isTimeInSlot(currentMinutes: number, startTime: string, endTime: string): boolean {
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;

    if (start <= end) {
      return currentMinutes >= start && currentMinutes < end;
    } else {
      // Midnight crossing: e.g. 22:00-02:00
      return currentMinutes >= start || currentMinutes < end;
    }
  }

  private getCurrentScheduleKey(): string | null {
    const schedules = this.availableSchedules();
    if (schedules.length === 0) return null;

    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // Most specific first: day+hour
    const dayHourMatch = schedules.find(s => {
      if (!s.key.startsWith('day+hour:')) return false;
      const parts = s.key.slice(9);
      const colonIdx = parts.indexOf(':');
      const date = parts.slice(0, colonIdx);
      const range = parts.slice(colonIdx + 1);
      if (date !== today) return false;
      const [startTime, endTime] = range.split('-');
      return this.isTimeInSlot(currentMinutes, startTime, endTime);
    });
    if (dayHourMatch) return dayHourMatch.key;

    // hour
    const hourMatch = schedules.find(s => {
      if (!s.key.startsWith('hour:')) return false;
      const range = s.key.slice(5);
      const [startTime, endTime] = range.split('-');
      return this.isTimeInSlot(currentMinutes, startTime, endTime);
    });
    if (hourMatch) return hourMatch.key;

    // day
    const dayMatch = schedules.find(s => s.key === `day:${today}`);
    if (dayMatch) return dayMatch.key;

    return null;
  }

  // --- Save / Cancel ---
  // "Visible" = non-draft AND non-garbage-sans-ventes (isTplVisible). Les filtres UI (label, schedule, search) ne restreignent JAMAIS le périmètre du save.
  save() {
    if (this.saving() || !this.hasChanges()) return;
    this.saving.set(true);
    const configs = this.tplConfigs();
    // Collect visible template IDs to exclude filtered-out templates from save
    const visibleIds = new Set<string>();
    for (const ev of this.events()) {
      for (const s of ev.sources) {
        for (const tpl of s.templates) {
          if (this.isTplVisible(tpl)) visibleIds.add(tpl._id);
        }
      }
    }
    const tpl: {id: string; scanNbr: number; nfcMode: string; colorConfirmation?: string}[] = [];
    for (const [id, c] of Object.entries(configs)) {
      // #9400 : les types d'accès Backstage ne sont pas envoyés, l'API les conserve tels qu'enregistrés.
      if (c.checked && !c.accreditation && visibleIds.has(id)) {
        // Le vert par défaut ('') n'est pas persisté : seul un choix explicite est stocké
        tpl.push({id, scanNbr: c.scanNbr, nfcMode: c.nfcMode, ...(c.colorConfirmation ? {colorConfirmation: c.colorConfirmation} : {})});
      }
    }
    this.boService.editTplList({tpl}).then(() => {
      const updated = {...this.tplConfigs()};
      for (const id of Object.keys(updated)) {
        const c = updated[id];
        updated[id] = {...c, original: {checked: c.checked, scanNbr: c.scanNbr, infinity: c.infinity, nfcMode: c.nfcMode, colorConfirmation: c.colorConfirmation}};
      }
      this.tplConfigs.set(updated);
      this.saving.set(false);
    }).catch((error: any) => { // any-justifié: erreur HTTP/CouchDB sans shape garantie
      this.saving.set(false);
      this.boService.errorLoad(error);
    });
  }

  cancel() {
    const configs = {...this.tplConfigs()};
    for (const id of Object.keys(configs)) {
      const c = configs[id];
      configs[id] = {...c, checked: c.original.checked, scanNbr: c.original.scanNbr, infinity: c.original.infinity, nfcMode: c.original.nfcMode, colorConfirmation: c.original.colorConfirmation};
    }
    this.tplConfigs.set(configs);
  }

}
